import React from "react";
import { motion } from "framer-motion";
import { Pencil, Printer, Boxes, Truck } from "lucide-react";

const STEPS = [
  {
    no: "01",
    Icon: Pencil,
    title: "Design lock",
    body: "Share artwork or use our in-house design desk. Dieline, barcodes, regulatory marks — all proofed.",
    day: "Day 1",
  },
  {
    no: "02",
    Icon: Printer,
    title: "Printing process",
    body: "Printing press — no cylinders. Every pouch can be a different SKU if you want.",
    day: "Day 2 – 3",
  },
  {
    no: "03",
    Icon: Boxes,
    title: "Lamination & pouching",
    body: "Multi-layer barrier films, food-safe adhesives, auto QC at every roll.",
    day: "Day 4 – 5",
  },
  {
    no: "04",
    Icon: Truck,
    title: "Dispatch",
    body: "Boxed, labelled, on the truck. Pan-India next-day for metros, 2–3 days elsewhere.",
    day: "Day 6 – 7",
  },
];

export default function ProcessTimeline() {
  return (
    <section
      id="process"
      data-testid="process-section"
      className="relative bg-white py-20 md:py-28 overflow-hidden"
    >
      <div className="absolute inset-0 pk-grid-bg opacity-40 pointer-events-none" />
      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-6 mb-14">
          <div className="col-span-12 md:col-span-7">
            <div className="text-[11px] font-bold uppercase tracking-[0.28em] text-[#1B3F8F] mb-4 flex items-center gap-3">
              <span className="inline-block w-8 h-px bg-[#F58220]" />
              How we move
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-black uppercase leading-[0.95] tracking-tighter">
              From <span className="text-[#F58220]">brief</span> to{" "}
              <span className="text-[#1B3F8F]">box</span>
              <br />in seven working days.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:flex md:items-end">
            <p className="text-base md:text-lg text-[#0a1128]/75 leading-relaxed font-medium">
              The fastest pouch line in India runs on a four-stage assembly.
              Every batch is tracked by job-number and you get a status update
              at every step.
            </p>
          </div>
        </div>

        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-[#d1d5db] bg-white"
          data-testid="process-grid"
        >
          {STEPS.map((s, i) => (
            <motion.div
              key={s.no}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: i * 0.1 }}
              data-testid={`process-step-${i}`}
              className={`relative p-7 md:p-9 ${
                i < STEPS.length - 1
                  ? "border-b md:border-b-0 md:border-r border-[#d1d5db]"
                  : ""
              } ${i >= 2 ? "md:border-t lg:border-t-0" : ""}`}
            >
              <div className="flex items-start justify-between mb-6">
                <span className="font-display text-xs font-bold tracking-[0.3em] text-[#1B3F8F]">
                  / {s.no}
                </span>
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-white bg-[#0a1128] px-2.5 py-1">
                  {s.day}
                </span>
              </div>
              <div className="w-12 h-12 mb-6 flex items-center justify-center bg-[#F58220] text-white">
                <s.Icon size={22} strokeWidth={1.7} />
              </div>
              <h3 className="font-display text-xl md:text-2xl font-black uppercase tracking-tight text-[#0a1128] mb-2">
                {s.title}
              </h3>
              <p className="text-sm leading-relaxed text-[#0a1128]/75 font-medium">
                {s.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
